import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  IGroupedPromotion,
  IPromotion,
  PromotionByPaymentMethod,
  PromotionType,
  Unit,
  getDiscountStr,
  getNPayBenefits,
  groupByPaymentMethod,
} from 'common/promotion';
import { IFareDetail, IScheduleDetail } from './types';
import { getPriceOfPerson } from './utils';

dayjs.extend(isSameOrAfter);

export function isPromotionTarget(
  promotion: IPromotion,
  schedules: IScheduleDetail[][],
  otaId: string,
  fareType: string,
) {
  const firstSch = schedules[0];
  const [generalFareTypeList, eventFareType] = fareType.split('/');
  return (
    (!promotion.departureStartDate ||
      schedules.every(([{ sdt }]) =>
        dayjs(sdt, 'YYYYMMDDHHmm').isSameOrAfter(promotion.departureStartDate),
      )) &&
    (!promotion.departureEndDate ||
      schedules.every(([{ sdt }]) =>
        dayjs(sdt, 'YYYYMMDDHHmm').isSameOrBefore(promotion.departureEndDate),
      )) &&
    (!promotion.fareTypes ||
      promotion.fareTypes.some(({ code }) => code === generalFareTypeList)) &&
    (!promotion.paymentMethods ||
      (eventFareType &&
        promotion.paymentMethods.some(({ code }) => code === eventFareType))) &&
    (!promotion.otas || promotion.otas.includes(otaId)) &&
    (!promotion.airlines ||
      schedules.every(flights =>
        flights.every(({ av }) => promotion.airlines?.includes(av)),
      )) &&
    (!promotion.originAirports ||
      promotion.originAirports.includes(firstSch[0].sa)) &&
    (!promotion.destinationAirports ||
      promotion.destinationAirports.includes(
        firstSch[firstSch.length - 1].ea,
      )) &&
    (!promotion.nonstopOnly || schedules.every(flights => flights.length === 1))
  );
}

function getNPayPromotions(
  promotions: IPromotion[],
  schedules: IScheduleDetail[][],
  fare: IFareDetail,
  originalPrice: number,
) {
  const [, eventFareType] = fare.FareType.split('/');
  return promotions.reduce<PromotionByPaymentMethod>(
    (promotionMap, promotion) => {
      if (
        !isPromotionTarget(promotion, schedules, fare.AgtCode, fare.FareType)
      ) {
        return promotionMap;
      }
      const totalDiscount = getNPayBenefits(promotion.benefit, originalPrice);
      if (!totalDiscount) return promotionMap;
      const { paymentMethods, benefit } = promotion;
      return groupByPaymentMethod(
        promotionMap,
        paymentMethods?.find(({ code }) => code === eventFareType),
        PromotionType.NAVER_PAY,
        benefit,
        totalDiscount,
      );
    },
    new Map(),
  );
}

export default function getPromotions(
  promotions: IPromotion[],
  schedules: IScheduleDetail[][],
  fare: IFareDetail,
) {
  const originalPrice = getPriceOfPerson(fare.Adult, true);
  const promotionMap = getNPayPromotions(
    promotions,
    schedules,
    fare,
    originalPrice,
  );
  const defaultBenefit = promotionMap
    .get(undefined)?.[1]
    .get(PromotionType.NAVER_PAY);

  if (defaultBenefit) {
    promotionMap.forEach(([, promotions], paymentMethod) => {
      if (!paymentMethod) return;
      const original = promotions.get(PromotionType.NAVER_PAY);
      if (!original) promotions.set(PromotionType.NAVER_PAY, defaultBenefit);
      else {
        original.totalDiscount += defaultBenefit.totalDiscount;
        original[Unit.PERCENT] += defaultBenefit[Unit.PERCENT] || 0;
        original[Unit.KRW] += defaultBenefit[Unit.KRW] || 0;
      }
    });
  }

  return Array.from(promotionMap)
    .map(([, [paymentMethod, promotions]]) =>
      Array.from(promotions).reduce<IGroupedPromotion>(
        (acc, discountInfo) => {
          const [type, promotion] = discountInfo;
          const summary = getDiscountStr(discountInfo);
          acc.summaryByType.push({ type, summary });
          acc.discountedPrice -= promotion.totalDiscount;
          return acc;
        },
        {
          otaId: fare.AgtCode,
          paymentMethod,
          summaryByType: [],
          discountedPrice: originalPrice,
        },
      ),
    )
    .sort((a, b) => a.discountedPrice - b.discountedPrice);
}
